import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { NOTIFICATION_STATUS_CODE } from '../enums/notification-status-code.enum';
import { NotificationCompletedListModel } from '../models/low-level/notification-completed.model';
import { NotificationIdentifierModel } from '../models/low-level/notification-identifier.model';
import { JsonNotificationStatusModel, NotificationStatusModel } from '../models/low-level/notification-status.model';
import { NotificationModel } from '../models/low-level/notification.model';
import { StatusModel } from '../models/low-level/status.model';

export interface NotificationStatusListModel {
    notificationStatusList: NotificationStatusModel[];
    lastSavedTimestamp: number;
}

export interface JsonNotificationStatusListModel {
    notificationStatusList: JsonNotificationStatusModel[];
    lastSavedTimestamp: number;
}

/**
 * Public contract of the notification-status service: the per-notification status data stored
 * locally plus the getters and mutators that components and services use for status tracking.
 *
 * Implemented by both the legacy `NotificationStatusService` (concurrency-store-backed) and the
 * NgRx-backed `NotificationStatusNgrxService`; consumers depend on this interface via
 * {@link NotificationStatusInstanceToken} rather than on a concrete class.
 */
export interface NotificationStatusInterface {
    init(): Observable<void>;

    getLatestNotificationStatusForId(notificationId: NotificationIdentifierModel): StatusModel | undefined;

    saveNotificationStatus(notificationId: NotificationIdentifierModel, newStatus: StatusModel): Observable<boolean>;

    addMissingStatusToList(notificationId: NotificationIdentifierModel, newStatus: StatusModel): void;

    setNotificationStatusList(notificationStatusList: NotificationStatusModel[]): void;

    saveNotificationStatusList(): Observable<void>;

    syncNotificationStatusList(): Observable<NotificationStatusListModel>;

    loadRecentlyCompletedNotifications(): Observable<NotificationCompletedListModel>;

    saveRecentlyCompletedNotifications(completedNotificationState: NotificationCompletedListModel): Observable<void>;

    addNotificationToRecentlyCompletedList(notification: NotificationModel): Observable<void>;

    isNotificationCompleted(notificationId: NotificationIdentifierModel): Observable<boolean>;

    findNotificationModelIndexById(notificationId: NotificationIdentifierModel): number;

    getNotificationStatusById(notificationId: NotificationIdentifierModel): NotificationStatusModel;

    getNotificationStatusByStatusType(
        notificationId: NotificationIdentifierModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined;

    getEscalated(notificationId: NotificationIdentifierModel): boolean;
    saveEscalated(notificationId: NotificationIdentifierModel, escalated: boolean): Observable<void>;

    getCancelled(notificationId: NotificationIdentifierModel): string;
    saveCancelled(notificationId: NotificationIdentifierModel, cancelledStatus: string): void;

    getUnreadIndicator(notificationId: NotificationIdentifierModel): boolean;
    saveUnreadIndicator(notificationId: NotificationIdentifierModel, unread: boolean): Observable<void>;

    getPausedIndicator(notificationId: NotificationIdentifierModel): boolean;
    savePausedIndicator(notificationId: NotificationIdentifierModel, paused: boolean): Observable<void>;

    saveCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): Observable<void>;
    removeCancellationConfirmationForInprogress(): void;
    getCancellationConfirmationForInprogress(notificationId: NotificationIdentifierModel): Promise<boolean>;

    removeOldNotificationsStatus(notificationArray: NotificationModel[]): Observable<void>;

    getNotificationByStatusInProgressOrToBeSigned(): NotificationStatusModel | undefined;

    getNotificationStatusIsPaused(): NotificationStatusModel | undefined;
}

/**
 * DI token for {@link NotificationStatusInterface}. Lets the app choose which implementation
 * to provide (legacy `NotificationStatusService` or the NgRx-based
 * `NotificationStatusNgrxService`) without consumers depending on a concrete class.
 */
export const NotificationStatusInstanceToken = new InjectionToken<NotificationStatusInterface>(
    'NotificationStatusInterface',
);
